import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { createAction } from '@ngrx/store';
import { of, race, zip } from 'rxjs';
import { catchError, map, mergeMap, repeat, switchMap, tap } from 'rxjs/operators';

import { AnalyticsFacade } from '@amfam/shared/analytics/lib/+state/analytics.facade';
import { fromRouterActions } from '@amfam/shared/utility/navigation/lib/+state/router-actions';
import { CopyService } from '@amfam/shared/utility/shared-services/lib/copy/copy.service';
import { Choice } from '@amfam/shared/utility/shared-services/lib/enums/choice.enum';
import { interpolate } from '@amfam/shared/utility/shared-services/lib/utils/string.utils';

import { AddressChange, ChangeRequest } from '../models/change-request.model';
import { PlchangerequestService } from '../services/plchangerequest.service';
import {
  addressChangeAgentEmail,
  addressChangeAgentEmailFailed,
  addressChangeAgentEmailSuccess,
  addressChangeANS,
  addressChangeANSFailed,
  addressChangeANSSuccess,
  addressChangeCustomerEmail,
  addressChangeLicensedAdvisorEmail,
  addressChangeLicensedAdvisorEmailFailed,
  addressChangeLicensedAdvisorEmailSuccess,
  addressChangeNotification,
  addressChangeNotificationFailed,
  coverageChangeAdvisorEmail,
  coverageChangeAdvisorEmailFailed,
  coverageChangeAdvisorEmailSuccess,
  coverageChangeAgentEmail,
  coverageChangeAgentEmailFailed,
  coverageChangeAgentEmailSuccess,
  coverageChangeANS,
  coverageChangeANSFailed,
  coverageChangeANSSuccess,
  coverageChangeCustomerEmail,
  coverageChangeNotification,
  coverageChangeNotificationFailed,
  driverLicenseChangeAdvisorEmail,
  driverLicenseChangeAdvisorEmailFailed,
  driverLicenseChangeAdvisorEmailSuccess,
  driverLicenseChangeANS,
  driverLicenseChangeANSFailed,
  driverLicenseChangeANSSuccess,
  driverLicenseChangeCustomerEmail,
  driverLicenseChangeNotify,
  driverLicenseChangeNotifyFailed,
  mortgageChangeAdvisorEmail,
  mortgageChangeAdvisorEmailFailed,
  mortgageChangeAdvisorEmailSuccess,
  mortgageChangeAgentEmail,
  mortgageChangeAgentEmailFailed,
  mortgageChangeAgentEmailSuccess,
  mortgageChangeANS,
  mortgageChangeANSFailed,
  mortgageChangeANSSuccess,
  mortgageChangeCustomerEmail,
  mortgageChangeNotification,
  mortgageChangeNotificationFailed,
  nameChangeAdvisorEmail,
  nameChangeAdvisorEmailFailed,
  nameChangeAdvisorEmailSuccess,
  nameChangeAgentEmail,
  nameChangeAgentEmailFailed,
  nameChangeAgentEmailSuccess,
  nameChangeANS,
  nameChangeANSFailed,
  nameChangeANSSuccess,
  nameChangeCustomerEmail,
  nameChangeNotification,
  nameChangeNotificationFailed,
  PolicyChangeRequestAdvisorEmailNotificationAction,
  PolicyChangeRequestANSNotificationAction,
  PolicyChangeRequestEmailNotificationAction,
  PolicyChangeRequestFailANSNotificationAction,
  PolicyChangeRequestFailEmailNotificationAction,
  PolicyChangeRequestNotificationAction,
  PolicyChangeRequestNotificationFailAction,
  PolicyChangeRequestSendAnalyticAction,
  PolicyChangeRequestSuccessANSNotificationAction,
  PolicyChangeRequestSuccessEmailNotificationAction,
  vinChangeAdvisorEmail,
  vinChangeAdvisorEmailFailed,
  vinChangeAdvisorEmailSuccess,
  vinChangeAgentEmail,
  vinChangeAgentEmailFailed,
  vinChangeAgentEmailSuccess,
  vinChangeANS,
  vinChangeANSFailed,
  vinChangeANSSuccess,
  vinChangeCustomerEmail,
  vinChangeNotification,
  vinChangeNotificationFailed
} from './policy-change.actions';

type AnalyticsCopy = Record<string, Record<string, string>>;

// Utility functions
function getSuccessAnalytics(payload: ChangeRequest, analytics: AnalyticsCopy) {
  const eventInfo: Record<string, unknown> = {};
  const completeMSRP = analytics.completeMSRP;
  if (payload.vinChange?.make && completeMSRP) {
    eventInfo.event = completeMSRP.event;
    eventInfo['vehicleYear'] = interpolate(completeMSRP.vehicleYear, {
      vehicle_year: payload.vinChange.newYear || ''
    });
    eventInfo['vehicleMake'] = interpolate(completeMSRP.vehicleMake, {
      vehicle_make: payload.vinChange.make || ''
    });
    eventInfo['vehicleModel'] = interpolate(completeMSRP.vehicleModel, {
      vehicle_model: payload.vinChange.model || ''
    });
  } else eventInfo.event = analytics?.complete?.event;
  return eventInfo;
}

function routeToConfirmation(agentId: string | null | undefined) {
  return fromRouterActions.Go({
    path: ['/policies/changerequest/confirmation'],
    extras: { queryParams: { agentId: agentId } }
  });
}

function getAddressTypes(addressChange: AddressChange, copyService: CopyService): string {
  const addressType = copyService.getCopy('policy.addressChange.analytics.addressTypes');
  const addressTypes: string[] = [];
  if (addressChange?.sendDocuments?.toUpperCase() === Choice.YES.toUpperCase())
    addressTypes.push(addressType.mailing);
  if (addressChange?.vehiclesGaraged?.toUpperCase() === Choice.YES.toUpperCase())
    addressTypes.push(addressType.parking);

  return addressTypes.length > 0 ? addressTypes.join(', ') : 'N/A';
}

/**
 * @description:
 */
export const notificationSplitter = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(PolicyChangeRequestNotificationAction),
      map(action => action.payload),
      mergeMap(payload => {
        return [
          PolicyChangeRequestEmailNotificationAction({ payload }),
          PolicyChangeRequestANSNotificationAction({ payload })
        ];
      }),
      catchError(error => of(PolicyChangeRequestNotificationFailAction(error)))
    );
  },
  { functional: true }
);

/**
 * @description:
 */
export const changePolicyDetailsEmailRequest = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(PolicyChangeRequestEmailNotificationAction),
      map(action => action.payload),
      mergeMap(payload =>
        plService.sendAgentEmailNotifications(payload).pipe(
          map(() => PolicyChangeRequestSuccessEmailNotificationAction({ payload })),
          catchError(error => of(PolicyChangeRequestNotificationFailAction(error)))
        )
      ),
      catchError(error => of(PolicyChangeRequestFailEmailNotificationAction(error)))
    );
  },
  { functional: true }
);

/**
 * @description:
 */
export const changePolicyDetailsANSRequest = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(PolicyChangeRequestANSNotificationAction),
      map(action => action.payload),
      switchMap(incomingPayload =>
        plService.sendANSNotifications(incomingPayload).pipe(
          map(() => PolicyChangeRequestSuccessANSNotificationAction()),
          catchError(error => of(PolicyChangeRequestNotificationFailAction(error)))
        )
      ),
      catchError(error => of(PolicyChangeRequestFailANSNotificationAction(error)))
    );
  },
  { functional: true }
);

export const changePolicyDetailsAdvisorEmailRequest = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(PolicyChangeRequestAdvisorEmailNotificationAction),
      map(action => action.payload),
      mergeMap(payload =>
        plService.sendAgentEmailNotifications(payload).pipe(
          map(() => PolicyChangeRequestSuccessEmailNotificationAction({ payload })),
          catchError(error => of(PolicyChangeRequestNotificationFailAction(error)))
        )
      ),
      catchError(error => of(PolicyChangeRequestFailEmailNotificationAction(error)))
    );
  },
  { functional: true }
);

/**
 * @description:
 */
export const changePolicyDetailsSuccess = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(PolicyChangeRequestSuccessEmailNotificationAction),
      map(action => action.payload),
      switchMap(incomingPayload => plService.generateCustomerEmail(incomingPayload))
    );
  },
  { dispatch: false, functional: true }
);

/**
 * @description:
 */
export const sendPolicyAnalytics = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(PolicyChangeRequestSendAnalyticAction),
      map(action => action.payload),
      map(incomingPayload => plService.sendAnalytic(incomingPayload))
    );
  },
  { dispatch: false, functional: true }
);

export const addressChangeNotificationSplitter = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(addressChangeNotification),
      map(action => action.payload),
      switchMap(payload => mapAddressChangeAction(payload)),
      catchError(error => of(addressChangeNotificationFailed(error)))
    );
  },
  { functional: true }
);

export const addressChangeAgentEmailEffect = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(addressChangeAgentEmail),
      map(action => {
        if (action.payload.requestChange) {
          action.payload.requestChange.isPartner = false;
        }
        return action.payload;
      }),
      switchMap(payload =>
        plService.sendAgentEmail(payload).pipe(
          map(() => addressChangeAgentEmailSuccess({ payload })),
          catchError(error => of(addressChangeAgentEmailFailed({ payload, status: error })))
        )
      ),
      catchError(error =>
        of(
          addressChangeAgentEmailFailed({
            payload: undefined as unknown as ChangeRequest,
            status: error
          })
        )
      )
    );
  },
  { functional: true }
);

export const addressChangeANSEffect = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(addressChangeANS),
      map(action => {
        if (action.payload.requestChange) {
          action.payload.requestChange.isPartner = false;
        }
        return action.payload;
      }),
      switchMap(payload =>
        plService
          .sendAgentEmail(payload, request => plService.sendANS(request))
          .pipe(
            map(() => addressChangeANSSuccess({ payload })),
            catchError(error => of(addressChangeANSFailed({ payload, status: error })))
          )
      ),
      catchError(error =>
        of(
          addressChangeANSFailed({ payload: undefined as unknown as ChangeRequest, status: error })
        )
      )
    );
  },
  { functional: true }
);

export const addressChangeCustomerEmailEffect = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(addressChangeCustomerEmail),
      map(action => action.payload),
      switchMap(payload =>
        plService.sendCustomerEmail(payload).pipe(
          map(() => routeToConfirmation(payload.addressChange?.agentId)),
          catchError(() => of(routeToConfirmation(payload.addressChange?.agentId)))
        )
      )
    );
  },
  { functional: true }
);

export const addressChangeError = createEffect(
  (
    actions$ = inject(Actions),
    copyService = inject(CopyService),
    analyticFacade = inject(AnalyticsFacade)
  ) => {
    return zip(
      actions$.pipe(ofType(addressChangeAgentEmailFailed)),
      actions$.pipe(ofType(addressChangeANSFailed))
    ).pipe(
      map(actions => actions[0]),
      tap(data => {
        const analytics = copyService.getCopy('policy.addressChange.analytics');
        analyticFacade.trackEvent({
          event: analytics.failure.event,
          address_type: data.payload?.addressChange
            ? getAddressTypes(data.payload.addressChange, copyService)
            : ''
        });
      }),
      map(data => addressChangeNotificationFailed({ status: data.status }))
    );
  },
  { functional: true }
);

export const addressChangeSuccessEffect = createEffect(
  (
    actions$ = inject(Actions),
    copyService = inject(CopyService),
    analyticFacade = inject(AnalyticsFacade)
  ) => {
    return race(
      actions$.pipe(ofType(addressChangeANSSuccess)),
      actions$.pipe(ofType(addressChangeAgentEmailSuccess))
    ).pipe(
      map(action => action.payload),
      tap(payload => {
        const analytics = copyService.getCopy('policy.addressChange.analytics');
        analyticFacade.trackEvent({
          event: analytics.complete.event,
          address_type: payload.addressChange
            ? getAddressTypes(payload.addressChange, copyService)
            : ''
        });
      }),
      switchMap(payload => of(addressChangeCustomerEmail({ payload }))),
      repeat()
    );
  },
  { functional: true }
);

export const addressChangeLicensedAdvisorEmailEffect = createEffect(
  (
    actions$ = inject(Actions),
    plService = inject(PlchangerequestService),
    copyService = inject(CopyService),
    analyticFacade = inject(AnalyticsFacade)
  ) => {
    return actions$.pipe(
      ofType(addressChangeLicensedAdvisorEmail),
      map(action => {
        if (action.payload.requestChange) {
          action.payload.requestChange.isPartner = true;
        }
        return action.payload;
      }),
      switchMap(payload =>
        (plService.sendAdvisorEmail(payload) ?? of(null)).pipe(
          map(() => addressChangeLicensedAdvisorEmailSuccess({ payload })),
          catchError(error => {
            const analytics = copyService.getCopy('policy.addressChange.analytics');
            analyticFacade.trackEvent({
              event: analytics.failure.event,
              address_type: payload.addressChange
                ? getAddressTypes(payload.addressChange, copyService)
                : '',
              partner_id: payload.requestChange?.partnerIds?.join(',')
            });
            return of(addressChangeLicensedAdvisorEmailFailed({ payload, status: error }));
          })
        )
      ),
      catchError(error =>
        of(
          addressChangeLicensedAdvisorEmailFailed({
            payload: undefined as unknown as ChangeRequest,
            status: error
          })
        )
      )
    );
  },
  { functional: true }
);

export const addressChangeLicensedAdvisorOnSuccess = createEffect(
  (
    actions$ = inject(Actions),
    copyService = inject(CopyService),
    analyticFacade = inject(AnalyticsFacade)
  ) => {
    return actions$.pipe(
      ofType(addressChangeLicensedAdvisorEmailSuccess),
      map(p => p.payload),
      tap(payload => {
        const analytics = copyService.getCopy('policy.addressChange.analytics');
        analyticFacade.trackEvent({
          event: analytics.complete.event,
          address_type: payload.addressChange
            ? getAddressTypes(payload.addressChange, copyService)
            : '',
          partner_id: payload.requestChange?.partnerIds?.join(',')
        });
      }),
      map(() => routeToConfirmation(null))
    );
  },
  { functional: true }
);

export const driverLicenseChangeSuccessEffect = createEffect(
  (
    actions$ = inject(Actions),
    copyService = inject(CopyService),
    analyticFacade = inject(AnalyticsFacade)
  ) => {
    return actions$.pipe(
      ofType(driverLicenseChangeANSSuccess),
      map(action => action.payload),
      tap(() => {
        const analytics = copyService.getCopy('policy.driverLicenseChange.analytics');
        analyticFacade.trackEvent({
          event: analytics.complete.event
        });
      }),
      switchMap(payload => of(driverLicenseChangeCustomerEmail({ payload })))
    );
  },
  { functional: true }
);

export const driverLicenseChangeANSEffect = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(driverLicenseChangeANS),
      map(action => action.payload),
      switchMap(payload =>
        plService
          .sendAgentEmail(payload, request => plService.sendANS(request))
          .pipe(
            map(() => driverLicenseChangeANSSuccess({ payload })),
            catchError(error => {
              return of(driverLicenseChangeANSFailed({ payload, status: error }));
            })
          )
      ),
      catchError(error => {
        return of(
          driverLicenseChangeANSFailed({
            payload: undefined as unknown as ChangeRequest,
            status: error
          })
        );
      })
    );
  },
  { functional: true }
);

export const driverLicenseChangeANSFailedEffect = createEffect(
  (
    actions$ = inject(Actions),
    copyService = inject(CopyService),
    analyticFacade = inject(AnalyticsFacade)
  ) => {
    return actions$.pipe(
      ofType(driverLicenseChangeANSFailed),
      tap(() => {
        const analytics = copyService.getCopy('policy.driverLicenseChange.analytics');
        analyticFacade.trackEvent({
          event: analytics.failure.event
        });
      })
    );
  },
  { dispatch: false, functional: true }
);

export const driverLicenseChangeCustomerEmailEffect = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(driverLicenseChangeCustomerEmail),
      map(action => action.payload),
      switchMap(payload => {
        const requestChange = payload.requestChange;
        const agentId = requestChange?.hasPartner
          ? null
          : (payload.driverLicenseChange?.agentId ?? null);
        return plService.sendCustomerEmail(payload).pipe(
          map(() => routeToConfirmation(agentId)),
          catchError(() => of(routeToConfirmation(agentId)))
        );
      })
    );
  },
  { functional: true }
);

export const driverLicenseChangeAdvisorEmailEffect = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(driverLicenseChangeAdvisorEmail),
      map(action => {
        if (action.payload.requestChange) {
          action.payload.requestChange.isPartner = true;
        }
        return action.payload;
      }),
      switchMap((payload: ChangeRequest) =>
        (plService.sendAdvisorEmail(payload) ?? of(null)).pipe(
          map(() => driverLicenseChangeAdvisorEmailSuccess({ payload })),
          catchError(error =>
            of(driverLicenseChangeAdvisorEmailFailed({ payload: payload, status: error }))
          )
        )
      ),
      catchError(error =>
        of(
          driverLicenseChangeAdvisorEmailFailed({
            payload: undefined as unknown as ChangeRequest,
            status: error
          })
        )
      )
    );
  },
  { functional: true }
);

export const driverLicenseChangeAdvisorEmailSuccessEffect = createEffect(
  (
    actions$ = inject(Actions),
    copyService = inject(CopyService),
    analyticFacade = inject(AnalyticsFacade)
  ) => {
    return actions$.pipe(
      ofType(driverLicenseChangeAdvisorEmailSuccess),
      map(action => action.payload),
      tap((payload: ChangeRequest) => {
        const analytics = copyService.getCopy('policy.driverLicenseChange.analytics');
        analyticFacade.trackEvent({
          event: analytics.complete.event,
          partner_id: payload.requestChange?.partnerIds?.join(',')
        });
      }),
      map(() => routeToConfirmation(null))
    );
  },
  { functional: true }
);

export const driverLicenseChangeAdvisorEmailFailedEffect = createEffect(
  (
    actions$ = inject(Actions),
    copyService = inject(CopyService),
    analyticFacade = inject(AnalyticsFacade)
  ) => {
    return actions$.pipe(
      ofType(driverLicenseChangeAdvisorEmailFailed),
      map(action => action.payload),
      tap((payload: ChangeRequest) => {
        const analytics: AnalyticsCopy = copyService.getCopy(
          'policy.driverLicenseChange.analytics'
        );
        analyticFacade.trackEvent({
          event: analytics?.failure?.event,
          partner_id: payload.requestChange?.partnerIds?.join(',')
        });
      })
    );
  },
  { dispatch: false, functional: true }
);

export const driverLicenseChangeNotifyEffect = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(driverLicenseChangeNotify),
      map(action => action.payload),
      switchMap(mapDriverLicenseAction),
      catchError(error =>
        of(
          driverLicenseChangeNotifyFailed({
            payload: undefined as unknown as ChangeRequest,
            status: error
          })
        )
      )
    );
  },
  { functional: true }
);

// Vin Change

export const vinChangeNotificationSplitterEffect = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(vinChangeNotification),
      map(action => action.payload),
      mergeMap(mapVinChangeAction),
      catchError(error =>
        of(
          vinChangeNotificationFailed({
            payload: undefined as unknown as ChangeRequest,
            status: error
          })
        )
      )
    );
  },
  { functional: true }
);

export const vinChangeAgentEmailEffect = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(vinChangeAgentEmail),
      map(action => {
        if (action.payload.requestChange) {
          action.payload.requestChange.isPartner = false;
        }
        return action.payload;
      }),
      switchMap((payload: ChangeRequest) =>
        plService.sendAgentEmail(payload).pipe(
          map(() => vinChangeAgentEmailSuccess({ payload })),
          catchError(error => of(vinChangeAgentEmailFailed({ payload, status: error })))
        )
      ),
      catchError(error =>
        of(
          vinChangeAgentEmailFailed({
            payload: undefined as unknown as ChangeRequest,
            status: error
          })
        )
      )
    );
  },
  { functional: true }
);

export const vinChangeAdvisorEmailEffect = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(vinChangeAdvisorEmail),
      map(action => {
        if (action.payload.requestChange) {
          action.payload.requestChange.isPartner = true;
        }
        return action.payload;
      }),
      switchMap((payload: ChangeRequest) =>
        (plService.sendAdvisorEmail(payload) ?? of(null)).pipe(
          map(() => vinChangeAdvisorEmailSuccess({ payload })),
          catchError(error => of(vinChangeAdvisorEmailFailed({ payload: payload, status: error })))
        )
      ),
      catchError(error =>
        of(
          vinChangeAdvisorEmailFailed({
            payload: undefined as unknown as ChangeRequest,
            status: error
          })
        )
      )
    );
  },
  { functional: true }
);

export const vinChangeAdvisorEmailSuccessEffect = createEffect(
  (
    actions$ = inject(Actions),
    copyService = inject(CopyService),
    analyticFacade = inject(AnalyticsFacade)
  ) => {
    return actions$.pipe(
      ofType(vinChangeAdvisorEmailSuccess),
      map(action => action.payload),
      tap((payload: ChangeRequest) => {
        const analytics = copyService.getCopy('policy.vinChange.analytics');
        analyticFacade.trackEvent({
          event: analytics.complete.event,
          partner_id: payload.requestChange?.partnerIds?.join(','),
          vehicle_year: String(payload.vinChange?.newYear || ''),
          vehicle_make: payload.vinChange?.make,
          vehicle_model: payload.vinChange?.model
        });
      }),
      map(() => routeToConfirmation(null))
    );
  },
  { functional: true }
);

export const vinChangeAdvisorEmailFailedEffect = createEffect(
  (
    actions$ = inject(Actions),
    copyService = inject(CopyService),
    analyticFacade = inject(AnalyticsFacade)
  ) => {
    return actions$.pipe(
      ofType(vinChangeAdvisorEmailFailed),
      map(action => action.payload),
      tap((payload: ChangeRequest) => {
        const analytics = copyService.getCopy('policy.vinChange.analytics');
        analyticFacade.trackEvent({
          event: analytics.failure.event as string,
          partner_id: payload.requestChange?.partnerIds?.join(',')
        });
      })
    );
  },
  { dispatch: false, functional: true }
);

export const vinChangeANSEffect = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(vinChangeANS),
      map(action => {
        if (action.payload.requestChange) {
          action.payload.requestChange.isPartner = false;
        }
        return action.payload;
      }),
      switchMap((payload: ChangeRequest) =>
        plService.sendANS(payload).pipe(
          map(() => vinChangeANSSuccess({ payload })),
          catchError(error => of(vinChangeANSFailed({ payload, status: error })))
        )
      ),
      catchError(error =>
        of(vinChangeANSFailed({ payload: undefined as unknown as ChangeRequest, status: error }))
      )
    );
  },
  { functional: true }
);

export const vinChangeCustomerEmailEffect = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(vinChangeCustomerEmail),
      map(action => action.payload),
      switchMap((payload: ChangeRequest) =>
        plService.sendCustomerEmail(payload).pipe(
          map(() => routeToConfirmation(payload.requestChange?.agentId)),
          catchError(() => of(routeToConfirmation(payload.requestChange?.agentId)))
        )
      )
    );
  },
  { functional: true }
);

export const vinChangeError = createEffect(
  (
    actions$ = inject(Actions),
    copyService = inject(CopyService),
    analyticFacade = inject(AnalyticsFacade)
  ) => {
    return zip(
      actions$.pipe(ofType(vinChangeAgentEmailFailed)),
      actions$.pipe(ofType(vinChangeANSFailed))
    ).pipe(
      map(actions => actions[0]),
      tap(_data => {
        const analytics = copyService.getCopy('policy.vinChange.analytics');
        analyticFacade.trackEvent({
          event: analytics.failure.event
        });
      }),
      map(data => vinChangeNotificationFailed({ payload: data.payload, status: data.status }))
    );
  },
  { functional: true }
);

export const vinChangeSuccessEffect = createEffect(
  (
    actions$ = inject(Actions),
    copyService = inject(CopyService),
    analyticFacade = inject(AnalyticsFacade)
  ) => {
    return race(
      actions$.pipe(ofType(vinChangeANSSuccess)),
      actions$.pipe(ofType(vinChangeAgentEmailSuccess))
    ).pipe(
      map(action => action.payload),
      tap(payload => {
        const analytics = copyService.getCopy('policy.vinChange.analytics');
        analyticFacade.trackEvent(getSuccessAnalytics(payload, analytics));
      }),
      switchMap(payload => of(vinChangeCustomerEmail({ payload }))),
      repeat()
    );
  },
  { functional: true }
);

// Name Change

export const nameChangeNotificationSplitterEffect = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(nameChangeNotification),
      map(action => action.payload),
      mergeMap(mapNameChangeAction),
      catchError(error =>
        of(
          nameChangeNotificationFailed({
            payload: undefined as unknown as ChangeRequest,
            status: error
          })
        )
      )
    );
  },
  { functional: true }
);

export const nameChangeAgentEmailEffect = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(nameChangeAgentEmail),
      map(action => {
        if (action.payload.requestChange) {
          action.payload.requestChange.isPartner = false;
        }
        return action.payload;
      }),
      switchMap((payload: ChangeRequest) =>
        plService.sendAgentEmail(payload).pipe(
          map(() => nameChangeAgentEmailSuccess({ payload })),
          catchError(error => of(nameChangeAgentEmailFailed({ payload, status: error })))
        )
      ),
      catchError(error =>
        of(
          nameChangeAgentEmailFailed({
            payload: undefined as unknown as ChangeRequest,
            status: error
          })
        )
      )
    );
  },
  { functional: true }
);

export const nameChangeAdvisorEmailEffect = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(nameChangeAdvisorEmail),
      map(action => {
        if (action.payload.requestChange) {
          action.payload.requestChange.isPartner = true;
        }
        return action.payload;
      }),
      switchMap((payload: ChangeRequest) =>
        (plService.sendAdvisorEmail(payload) ?? of(null)).pipe(
          map(() => nameChangeAdvisorEmailSuccess({ payload })),
          catchError(error => of(nameChangeAdvisorEmailFailed({ payload: payload, status: error })))
        )
      ),
      catchError(error =>
        of(
          nameChangeAdvisorEmailFailed({
            payload: undefined as unknown as ChangeRequest,
            status: error
          })
        )
      )
    );
  },
  { functional: true }
);

export const nameChangeAdvisorEmailSuccessEffect = createEffect(
  (
    actions$ = inject(Actions),
    copyService = inject(CopyService),
    analyticFacade = inject(AnalyticsFacade)
  ) => {
    return actions$.pipe(
      ofType(nameChangeAdvisorEmailSuccess),
      map(action => action.payload),
      tap((payload: ChangeRequest) => {
        const analytics = copyService.getCopy('policy.nameChange.analytics');
        analyticFacade.trackEvent({
          event: analytics.complete.event,
          partner_id: payload.requestChange?.partnerIds?.join(',')
        });
      }),
      map(() => routeToConfirmation(null))
    );
  },
  { functional: true }
);

export const nameChangeAdvisorEmailFailedEffect = createEffect(
  (
    actions$ = inject(Actions),
    copyService = inject(CopyService),
    analyticFacade = inject(AnalyticsFacade)
  ) => {
    return actions$.pipe(
      ofType(nameChangeAdvisorEmailFailed),
      map(action => action.payload),
      tap((payload: ChangeRequest) => {
        const analytics = copyService.getCopy('policy.nameChange.analytics');
        analyticFacade.trackEvent({
          event: analytics.failure.event,
          partner_id: payload.requestChange?.partnerIds?.join(',')
        });
      })
    );
  },
  { dispatch: false, functional: true }
);

export const nameChangeANSEffect = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(nameChangeANS),
      map(action => {
        if (action.payload.requestChange) {
          action.payload.requestChange.isPartner = false;
        }
        return action.payload;
      }),
      switchMap((payload: ChangeRequest) =>
        plService
          .sendAgentEmail(payload, request => plService.sendANS(request))
          .pipe(
            map(() => nameChangeANSSuccess({ payload })),
            catchError(error => of(nameChangeANSFailed({ payload, status: error })))
          )
      ),
      catchError(error =>
        of(nameChangeANSFailed({ payload: undefined as unknown as ChangeRequest, status: error }))
      )
    );
  },
  { functional: true }
);

export const nameChangeCustomerEmailEffect = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(nameChangeCustomerEmail),
      map(action => action.payload),
      switchMap((payload: ChangeRequest) =>
        plService.sendCustomerEmail(payload).pipe(
          map(() => routeToConfirmation(payload.requestChange?.agentId)),
          catchError(() => of(routeToConfirmation(payload.requestChange?.agentId)))
        )
      )
    );
  },
  { functional: true }
);

export const nameChangeError = createEffect(
  (
    actions$ = inject(Actions),
    copyService = inject(CopyService),
    analyticFacade = inject(AnalyticsFacade)
  ) => {
    return zip(
      actions$.pipe(ofType(nameChangeAgentEmailFailed)),
      actions$.pipe(ofType(nameChangeANSFailed))
    ).pipe(
      map(actions => actions[0]),
      tap(() => {
        const analytics = copyService.getCopy('policy.nameChange.analytics');
        analyticFacade.trackEvent({
          event: analytics.failure.event
        });
      }),
      map(data => nameChangeNotificationFailed({ payload: data.payload, status: data.status }))
    );
  },
  { functional: true }
);

export const nameChangeSuccessEffect = createEffect(
  (actions$ = inject(Actions)) => {
    return race(
      actions$.pipe(ofType(nameChangeANSSuccess)),
      actions$.pipe(ofType(nameChangeAgentEmailSuccess))
    ).pipe(
      map(action => action.payload),
      switchMap(payload => of(nameChangeCustomerEmail({ payload }))),
      repeat()
    );
  },
  { functional: true }
);

// Mortgage Change

export const mortgageChangeNotificationSplitterEffect = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(mortgageChangeNotification),
      map(action => action.payload),
      mergeMap(mapMortgageChangeAction),
      catchError(error =>
        of(
          mortgageChangeNotificationFailed({
            payload: undefined as unknown as ChangeRequest,
            status: error
          })
        )
      )
    );
  },
  { functional: true }
);

export const mortgageChangeAgentEmailEffect = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(mortgageChangeAgentEmail),
      map(action => {
        if (action.payload.requestChange) {
          action.payload.requestChange.isPartner = false;
        }
        return action.payload;
      }),
      switchMap((payload: ChangeRequest) =>
        plService.sendAgentEmail(payload).pipe(
          map(() => mortgageChangeAgentEmailSuccess({ payload })),
          catchError(error => of(mortgageChangeAgentEmailFailed({ payload, status: error })))
        )
      ),
      catchError(error =>
        of(
          mortgageChangeAgentEmailFailed({
            payload: undefined as unknown as ChangeRequest,
            status: error
          })
        )
      )
    );
  },
  { functional: true }
);

export const mortgageChangeAdvisorEmailEffect = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(mortgageChangeAdvisorEmail),
      map(action => {
        if (action.payload.requestChange) {
          action.payload.requestChange.isPartner = true;
        }
        return action.payload;
      }),
      switchMap((payload: ChangeRequest) =>
        (plService.sendAdvisorEmail(payload) ?? of(null)).pipe(
          map(() => mortgageChangeAdvisorEmailSuccess({ payload })),
          catchError(error =>
            of(mortgageChangeAdvisorEmailFailed({ payload: payload, status: error }))
          )
        )
      ),
      catchError(error =>
        of(
          mortgageChangeAdvisorEmailFailed({
            payload: undefined as unknown as ChangeRequest,
            status: error
          })
        )
      )
    );
  },
  { functional: true }
);

export const mortgageChangeANSEffect = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(mortgageChangeANS),
      map(action => {
        if (action.payload.requestChange) {
          action.payload.requestChange.isPartner = false;
        }
        return action.payload;
      }),
      switchMap((payload: ChangeRequest) =>
        plService.sendANS(payload).pipe(
          map(() => mortgageChangeANSSuccess({ payload })),
          catchError(error => of(mortgageChangeANSFailed({ payload, status: error })))
        )
      ),
      catchError(error =>
        of(
          mortgageChangeANSFailed({ payload: undefined as unknown as ChangeRequest, status: error })
        )
      )
    );
  },
  { functional: true }
);

export const mortgageChangeCustomerEmailEffect = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(mortgageChangeCustomerEmail),
      map(action => action.payload),
      switchMap((payload: ChangeRequest) =>
        plService.sendCustomerEmail(payload).pipe(
          map(() =>
            routeToConfirmation(
              payload.mortgageChange?.isAfi ? payload.requestChange?.agentId : null
            )
          ),
          catchError(() =>
            of(
              routeToConfirmation(
                payload.mortgageChange?.isAfi ? payload.requestChange?.agentId : null
              )
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const mortgageChangeError = createEffect(
  (actions$ = inject(Actions)) => {
    return zip(
      actions$.pipe(ofType(mortgageChangeAgentEmailFailed)),
      actions$.pipe(ofType(mortgageChangeANSFailed))
    ).pipe(
      map(actions => actions[0]),
      map(data => mortgageChangeNotificationFailed({ payload: data.payload, status: data.status }))
    );
  },
  { functional: true }
);

export const mortgageChangeSuccessEffect = createEffect(
  (actions$ = inject(Actions)) => {
    return race(
      actions$.pipe(ofType(mortgageChangeANSSuccess)),
      actions$.pipe(ofType(mortgageChangeAgentEmailSuccess))
    ).pipe(
      map(action => action.payload),
      switchMap(payload => of(mortgageChangeCustomerEmail({ payload }))),
      repeat()
    );
  },
  { functional: true }
);

// Coverage Change

export const coverageChangeNotificationSplitterEffect = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(coverageChangeNotification),
      map(action => action.payload),
      mergeMap(mapCoverageChangeAction),
      catchError(error =>
        of(
          coverageChangeNotificationFailed({
            payload: undefined as unknown as ChangeRequest,
            status: error
          })
        )
      )
    );
  },
  { functional: true }
);

export const coverageChangeAgentEmailEffect = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(coverageChangeAgentEmail),
      map(action => {
        if (action.payload.requestChange) {
          action.payload.requestChange.isPartner = false;
        }
        return action.payload;
      }),
      switchMap((payload: ChangeRequest) =>
        plService.sendAgentEmail(payload).pipe(
          map(() => coverageChangeAgentEmailSuccess({ payload })),
          catchError(error => of(coverageChangeAgentEmailFailed({ payload, status: error })))
        )
      ),
      catchError(error =>
        of(
          coverageChangeAgentEmailFailed({
            payload: undefined as unknown as ChangeRequest,
            status: error
          })
        )
      )
    );
  },
  { functional: true }
);

export const coverageChangeAdvisorEmailEffect = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(coverageChangeAdvisorEmail),
      map(action => {
        if (action.payload.requestChange) {
          action.payload.requestChange.isPartner = true;
        }
        return action.payload;
      }),
      switchMap((payload: ChangeRequest) =>
        (plService.sendAdvisorEmail(payload) ?? of(null)).pipe(
          map(() => coverageChangeAdvisorEmailSuccess({ payload })),
          catchError(error =>
            of(coverageChangeAdvisorEmailFailed({ payload: payload, status: error }))
          )
        )
      ),
      catchError(error =>
        of(
          coverageChangeAdvisorEmailFailed({
            payload: undefined as unknown as ChangeRequest,
            status: error
          })
        )
      )
    );
  },
  { functional: true }
);

export const coverageChangeAdvisorEmailSuccessEffect = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(coverageChangeAdvisorEmailSuccess),
      map(action => action.payload),
      map(() => routeToConfirmation(null))
    );
  },
  { functional: true }
);

export const coverageChangeANSEffect = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(coverageChangeANS),
      map(action => {
        if (action.payload.requestChange) {
          action.payload.requestChange.isPartner = false;
        }
        return action.payload;
      }),
      switchMap((payload: ChangeRequest) =>
        plService.sendANS(payload).pipe(
          map(() => coverageChangeANSSuccess({ payload })),
          catchError(error => of(coverageChangeANSFailed({ payload, status: error })))
        )
      ),
      catchError(error =>
        of(
          coverageChangeANSFailed({ payload: undefined as unknown as ChangeRequest, status: error })
        )
      )
    );
  },
  { functional: true }
);

export const coverageChangeCustomerEmailEffect = createEffect(
  (actions$ = inject(Actions), plService = inject(PlchangerequestService)) => {
    return actions$.pipe(
      ofType(coverageChangeCustomerEmail),
      map(action => action.payload),
      switchMap((payload: ChangeRequest) =>
        plService.sendCustomerEmail(payload).pipe(
          map(() =>
            routeToConfirmation(
              payload.requestChange?.hasAfi && !payload.requestChange?.hasPartner
                ? payload.requestChange?.agentId
                : null
            )
          ),
          catchError(() =>
            of(
              routeToConfirmation(
                payload.requestChange?.hasAfi && !payload.requestChange?.hasPartner
                  ? payload.requestChange?.agentId
                  : null
              )
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const coverageChangeError = createEffect(
  (actions$ = inject(Actions)) => {
    return zip(
      actions$.pipe(ofType(coverageChangeAgentEmailFailed)),
      actions$.pipe(ofType(coverageChangeANSFailed))
    ).pipe(
      map(actions => actions[0]),
      map(data => coverageChangeNotificationFailed({ payload: data.payload, status: data.status }))
    );
  },
  { functional: true }
);

export const coverageChangeSuccessEffect = createEffect(
  (actions$ = inject(Actions)) => {
    return race(
      actions$.pipe(ofType(coverageChangeANSSuccess)),
      actions$.pipe(ofType(coverageChangeAgentEmailSuccess))
    ).pipe(
      map(action => action.payload),
      switchMap(payload => of(coverageChangeCustomerEmail({ payload }))),
      repeat()
    );
  },
  { functional: true }
);

function mapDriverLicenseAction(payload: ChangeRequest) {
  const requestChange = payload.requestChange;
  if (requestChange?.hasAfi && requestChange.hasPartner) {
    return [driverLicenseChangeANS({ payload }), driverLicenseChangeAdvisorEmail({ payload })];
  } else if (requestChange?.hasPartner) return [driverLicenseChangeAdvisorEmail({ payload })];
  else if (requestChange?.hasAfi) return [driverLicenseChangeANS({ payload })];
  else return [createAction('[Empty] Empty Action')()];
}

function mapAddressChangeAction(payload: ChangeRequest) {
  const requestChange = payload.requestChange;
  if (requestChange?.hasAfi && requestChange.hasPartner) {
    return [
      addressChangeLicensedAdvisorEmail({ payload }),
      addressChangeAgentEmail({ payload }),
      addressChangeANS({ payload })
    ];
  } else if (requestChange?.hasPartner) return [addressChangeLicensedAdvisorEmail({ payload })];
  else if (requestChange?.hasAfi)
    return [addressChangeAgentEmail({ payload }), addressChangeANS({ payload })];
  else return [createAction('[Empty] Empty Action')()];
}

function mapNameChangeAction(payload: ChangeRequest) {
  const requestChange = payload.requestChange;
  if (requestChange?.hasAfi) {
    return [nameChangeAgentEmail({ payload }), nameChangeANS({ payload })];
  } else return [createAction('[Empty] Empty Action')()];
}

function mapMortgageChangeAction(payload: ChangeRequest) {
  if (payload.mortgageChange?.isAfi) {
    return [mortgageChangeAgentEmail({ payload }), mortgageChangeANS({ payload })];
  } else return [createAction('[Empty] Empty Action')()];
}

function mapCoverageChangeAction(payload: ChangeRequest) {
  const requestChange = payload.requestChange;
  if (requestChange?.hasAfi)
    return [
      coverageChangeAgentEmail({ payload: payload }),
      coverageChangeANS({ payload: payload })
    ];
  else return [createAction('[Empty] Empty Action')()];
}

function mapVinChangeAction(payload: ChangeRequest) {
  if (payload.vinChange?.isAfi === null) return [createAction('[Empty] Empty Action')()];
  if (payload.vinChange?.isAfi) {
    return [vinChangeAgentEmail({ payload }), vinChangeANS({ payload })];
  } else return [vinChangeAdvisorEmail({ payload })];
}
